import React, { useState, useEffect, useMemo, useRef } from 'react'
import { connect } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import styles from './NewNavImproved.module.scss'
import {
  Home,
  Building,
  Users,
  Store,
  Shield,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  FileText as FileTextIcon,
  Receipt,
  Calculator,
  DollarSign,
  ClipboardList,
  FilePlus,
  UserPlus,
  Package,
  FolderOpen,
  FolderKanban,
  ShoppingBag,
  LockKeyhole,
  Search,
  FolderPlus
} from 'lucide-react'
import { logout, openMainModal, closeMainModal, expandedSideBar, openFeedTextModal } from '../../../store/actions/index.js'
import { buildProfileUrl, getProfileTabFromQuery, PROFILE_SUBITEM_TAB_MAPPING, PROFILE_TAB_TO_NAV_ITEM, getProfilePathInfo, getUserDisplayName } from '../../../utils/navigationHelpers.ts'
import { fireAnalytics } from '../../../utils/firebase.js'
import { getSessionId, getDeviceCategory, getPreviousPage, setPreviousPage } from '../../../utils/analyticsHelpers.ts'

interface NewNavImprovedProps {
  userData?: any
  profileVisualizerData?: any
  logout: (redirect: boolean, navigate: any) => void
  openMainModal: (config: any) => void
  closeMainModal: () => void
  openFeedTextModal: (config: { text: boolean }) => void
  sideNavBarExpanded: boolean
  expandedSideBar: (value: boolean) => void
}

interface NavSubItem {
  id: string
  label: string
  icon: React.ReactNode
  route: string
  directRoute?: string
}

interface NavItem {
  id: string
  label: string
  icon: React.ReactNode
  activeIcon: React.ReactNode
  color: string
  badge?: number
  adminOnly?: boolean
  subItems?: NavSubItem[]
  hasSubmenu?: boolean
}

const TruncatedText: React.FC<{ text: string; className?: string }> = ({ text, className }) => {
  const ref = useRef<HTMLSpanElement>(null)
  const [isTruncated, setIsTruncated] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const check = () => setIsTruncated(el.scrollWidth > el.clientWidth)
    check()
    const ro = new ResizeObserver(check)
    ro.observe(el)
    return () => ro.disconnect()
  }, [text])

  const showTooltip = isTruncated && isHovered

  return (
    <span
      className={styles.truncatedTextWrapper}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <span ref={ref} className={className}>
        {text}
      </span>
      {showTooltip && (
        <div className={styles.truncatedTooltip}>
          <div className={styles.simpleTooltip}>{text}</div>
        </div>
      )}
    </span>
  )
}

const NewNavImproved: React.FC<NewNavImprovedProps> = ({
  userData,
  profileVisualizerData,
  logout,
  openMainModal,
  closeMainModal,
  openFeedTextModal,
  sideNavBarExpanded,
  expandedSideBar
}) => {
  const [isActive, setIsActive] = useState<string | null>(null)
  const [expandedSubmenus, setExpandedSubmenus] = useState<Set<string>>(new Set())
  const [manuallyOpenedSubmenus, setManuallyOpenedSubmenus] = useState<Set<string>>(new Set())
  const [activeProfileTab, setActiveProfileTab] = useState<string | null>(null)
  const isNavExpanded = sideNavBarExpanded
  const toggleNav = () => expandedSideBar(!sideNavBarExpanded)
  const navigate = useNavigate()
  const location = useLocation()
  const profilePathInfo = useMemo(() => getProfilePathInfo(location.pathname), [location.pathname])
  
  const isUserAdmin = userData?.rols && userData.rols.filter((rol: any) => rol.name === "ADMIN").length > 0

  const navItems: NavItem[] = [
    {
      id: 'dashboard',
      label: 'Inicio',
      icon: <Home size={20} />,
      activeIcon: <Home size={20} />,
      color: '#234ba2'
    },
    {
      id: 'publicar',
      label: 'Publicar',
      icon: <FolderPlus size={20} />,
      activeIcon: <FolderPlus size={20} />,
      color: '#059669',
      hasSubmenu: true,
      subItems: [
        {
          id: 'presupuestos-obra',
          label: 'Presupuestos para mi obra',
          icon: <FileTextIcon size={16} />,
          route: '/obrasprivadas/crear',
          directRoute: '/obrasprivadas/crear'
        },
        {
          id: 'cotizar-materiales',
          label: 'Pedir una cotización de materiales',
          icon: <DollarSign size={16} />,
          route: '/cotizaciondemateriales/crear',
          directRoute: '/cotizaciondemateriales/crear'
        },
        {
          id: 'publicar-oferta-trabajo',
          label: 'Vacante laboral',
          icon: <FilePlus size={16} />,
          route: '/mercado-de-trabajo/crearoferta',
          directRoute: '/mercado-de-trabajo/crearoferta'
        },
        {
          id: 'ofrecer-servicios',
          label: 'Posteos o productos/servicios (se publican en Explorar)',
          icon: <Package size={16} />,
          route: '#',
          directRoute: '#'
        }
      ]
    },
    {
      id: 'explorar',
      label: 'Explorar',
      icon: <Search size={20} />,
      activeIcon: <Search size={20} />,
      color: '#234ba2',
      hasSubmenu: true,
      subItems: [
        {
          id: 'cotizar-obras',
          label: 'Cotizar en obras privadas',
          icon: <Receipt size={16} />,
          route: '/obrasprivadas/explorar',
          directRoute: '/obrasprivadas/explorar'
        },
        {
          id: 'obras-publicas',
          label: 'Obras públicas',
          icon: <Building size={16} />,
          route: '/obraspublicas',
          directRoute: '/obraspublicas'
        },
        {
          id: 'cotizaciones-materiales',
          label: 'Cotizaciones de materiales',
          icon: <ClipboardList size={16} />,
          route: '/cotizaciondemateriales/explorar',
          directRoute: '/cotizaciondemateriales/explorar'
        },
        {
          id: 'encontrar-profesionales',
          label: 'Perfiles',
          icon: <Users size={16} />,
          route: '/buscar-perfiles',
          directRoute: '/buscar-perfiles'
        },
        {
          id: 'explorar-servicios',
          label: 'Posteos, servicios y productos',
          icon: <Store size={16} />,
          route: '/marketplace/explorar',
          directRoute: '/marketplace/explorar'
        },
        {
          id: 'postularme-ofertas',
          label: 'Postularme a ofertas de trabajo',
          icon: <UserPlus size={16} />,
          route: '/bolsa-trabajo',
          directRoute: '/bolsa-trabajo'
        }
      ]
    },
    {
      id: 'mis-contenidos',
      label: 'Mis contenidos',
      icon: <FolderKanban size={20} />,
      activeIcon: <FolderKanban size={20} />,
      color: '#1a3a7a',
      hasSubmenu: true,
      subItems: [
        {
          id: 'mis-proyectos',
          label: 'Proyectos en Edify',
          icon: <FolderOpen size={16} />,
          route: '#',
          directRoute: '#'
        },
        {
          id: 'mis-cotizaciones-materiales',
          label: 'Cotizaciones de materiales',
          icon: <FolderKanban size={16} />,
          route: '#',
          directRoute: '#'
        },
        {
          id: 'mis-postulaciones',
          label: 'Postulaciones',
          icon: <LockKeyhole size={16} />,
          route: '#',
          directRoute: '#'
        },
        {
          id: 'mis-productos-servicios',
          label: 'Productos/servicios',
          icon: <ShoppingBag size={16} />,
          route: '#',
          directRoute: '#'
        }
      ]
    },
    {
      id: 'herramientas',
      label: 'Herramientas',
      icon: <Calculator size={20} />,
      activeIcon: <Calculator size={20} />,
      color: '#45a7ff',
      hasSubmenu: true,
      subItems: [
        {
          id: 'estimacion-costos',
          label: 'Estimación de costos de materiales',
          icon: <Calculator size={16} />,
          route: '/cotizaciondemateriales/estimacion-costos',
          directRoute: '/cotizaciondemateriales/estimacion-costos'
        }
      ]
    },
    {
      id: 'admin',
      label: 'Admin',
      icon: <Shield size={20} />,
      activeIcon: <Shield size={20} />,
      color: '#dc2626',
      adminOnly: true,
      hasSubmenu: true,
      subItems: [
        {
          id: 'backoffice',
          label: 'Backoffice',
          icon: <Shield size={16} />,
          route: '/backoffice',
          directRoute: '/backoffice'
        }
      ]
    }
  ]

  // Filtrar items según permisos
  const filteredNavItems = navItems.filter(item => !item.adminOnly || isUserAdmin)

  // Función para detectar si algún subitem está activo
  const isSubItemActive = (item: NavItem): boolean => {
    if (!item.subItems) return false
    const pathname = location.pathname
    const searchParams = new URLSearchParams(location.search)
    
    if (activeProfileTab && PROFILE_TAB_TO_NAV_ITEM[activeProfileTab] === item.id) {
      return true
    }
    
    return item.subItems.some(subItem => {
      const mappedTab = PROFILE_SUBITEM_TAB_MAPPING[subItem.id]
      if (mappedTab && activeProfileTab) {
        return activeProfileTab === mappedTab
      }
      if (mappedTab && userData) {
        const pathSegments = pathname.split('/').filter(Boolean)
        if (pathSegments[0] === 'profesionales' && pathSegments[1] === String(userData.id)) {
          const currentTab = getProfileTabFromQuery(searchParams)
          return currentTab === mappedTab
        }
      }
      
      const targetRoute = subItem.directRoute || subItem.route
      if (targetRoute && targetRoute !== '#') {
        if (targetRoute === '/profesionales') {
          return pathname === targetRoute
        }
        return pathname === targetRoute || pathname.startsWith(targetRoute)
      }
      return false
    })
  }

  // Función para obtener el subitem activo
  const getActiveSubItem = (item: NavItem): string | null => {
    if (!item.subItems) return null
    const pathname = location.pathname
    const searchParams = new URLSearchParams(location.search)
    
    if (activeProfileTab) {
      const profileMatch = item.subItems.find(subItem => PROFILE_SUBITEM_TAB_MAPPING[subItem.id] === activeProfileTab)
      if (profileMatch) {
        return profileMatch.id
      }
    }
    
    const activeSubItem = item.subItems.find(subItem => {
      const mappedTab = PROFILE_SUBITEM_TAB_MAPPING[subItem.id]
      if (mappedTab && userData) {
        const pathSegments = pathname.split('/').filter(Boolean)
        if (pathSegments[0] === 'profesionales' && pathSegments[1] === String(userData.id)) {
          const currentTab = getProfileTabFromQuery(searchParams)
          return currentTab === mappedTab
        }
      }
      
      const targetRoute = subItem.directRoute || subItem.route
      if (targetRoute && targetRoute !== '#') {
        if (targetRoute === '/profesionales') {
          return pathname === targetRoute
        }
        return pathname === targetRoute || pathname.startsWith(targetRoute)
      }
      return false
    })
    
    return activeSubItem ? activeSubItem.id : null
  }

  const handleRedirect = (item: NavItem) => {
    if (item.id === 'dashboard') {
      // Trackear click en nav item dashboard
      fireAnalytics('nav_item_clicked', {
        edify_user_id: userData?.id || '',
        session_id: getSessionId(),
        nav_item_id: item.id,
        nav_item_label: item.label,
        previous_page: getPreviousPage(),
        device_category: getDeviceCategory(),
        category: 'navigation',
        action: 'nav_item_click',
        source: 'navigation'
      })
      
      setPreviousPage(location.pathname)
      navigate('/dashboard', { 
        state: { fromDashboard: true } 
      })
    } else if (item.hasSubmenu && isNavExpanded) {
      const newExpanded = new Set(expandedSubmenus)
      if (newExpanded.has(item.id)) {
        // Cerrar el submenú actual
        newExpanded.delete(item.id)
        setManuallyOpenedSubmenus(prev => {
          const newManual = new Set(prev)
          newManual.delete(item.id)
          return newManual
        })
      } else {
        // Trackear expansión de submenú
        fireAnalytics('nav_item_clicked', {
          edify_user_id: userData?.id || '',
          session_id: getSessionId(),
          nav_item_id: item.id,
          nav_item_label: item.label,
          nav_action: 'expand_submenu',
          previous_page: getPreviousPage(),
          device_category: getDeviceCategory(),
          category: 'navigation',
          action: 'nav_item_click',
          source: 'navigation'
        })
        
        // Cerrar todos los otros submenús y abrir solo este
        newExpanded.clear()
        newExpanded.add(item.id)
        setManuallyOpenedSubmenus(new Set([item.id]))
      }
      setExpandedSubmenus(newExpanded)
    }
  }

  const handleSubItemRedirect = (subItem: NavSubItem) => {
    // Encontrar el item padre del subitem
    const parentItem = navItems.find(item => 
      item.subItems?.some(sub => sub.id === subItem.id)
    )
    
    // Caso especial: ofrecer servicios/productos debe abrir el modal
    if (subItem.id === 'ofrecer-servicios') {
      fireAnalytics('nav_subitem_clicked', {
        edify_user_id: userData?.id || '',
        session_id: getSessionId(),
        nav_item_id: parentItem?.id || '',
        nav_item_label: parentItem?.label || '',
        nav_subitem_id: subItem.id,
        nav_subitem_label: subItem.label,
        nav_action: 'open_modal',
        previous_page: getPreviousPage(),
        device_category: getDeviceCategory(),
        category: 'navigation',
        action: 'nav_subitem_click',
        source: 'navigation'
      })
      
      openFeedTextModal({ text: true })
      return
    }

    // Casos especiales: navegación a tabs del perfil
    const profileTab = PROFILE_SUBITEM_TAB_MAPPING[subItem.id];
    if (profileTab) {
      const { isProfile, userId: pathUserId, userName: pathUserName } = profilePathInfo;
      const ownUserId = userData?.id ? String(userData.id) : null;
      const ownDisplayName = getUserDisplayName(userData);

      const candidateProfileData = (() => {
        if (!isProfile) return userData;
        if (ownUserId && pathUserId === ownUserId) return userData;
        if (profileVisualizerData && profileVisualizerData.id && String(profileVisualizerData.id) === pathUserId) {
          return profileVisualizerData;
        }
        return profileVisualizerData;
      })();

      const candidateId = candidateProfileData?.id ? String(candidateProfileData.id) : (isProfile ? pathUserId : ownUserId);
      const candidateName = getUserDisplayName(candidateProfileData) || (isProfile ? pathUserName : null) || ownDisplayName || pathUserName || 'perfil';

      if (candidateId && candidateName) {
        if (ownUserId && candidateId === ownUserId) {
          setActiveProfileTab(profileTab);
          if (PROFILE_TAB_TO_NAV_ITEM[profileTab] && isNavExpanded) {
            setExpandedSubmenus(new Set([PROFILE_TAB_TO_NAV_ITEM[profileTab]]));
          }
        } else {
          setActiveProfileTab(null);
        }
        // Trackear click en subitem que navega a perfil
        fireAnalytics('nav_subitem_clicked', {
          edify_user_id: userData?.id || '',
          session_id: getSessionId(),
          nav_item_id: parentItem?.id || '',
          nav_item_label: parentItem?.label || '',
          nav_subitem_id: subItem.id,
          nav_subitem_label: subItem.label,
          nav_action: 'navigate_to_profile',
          profile_tab: profileTab,
          previous_page: getPreviousPage(),
          device_category: getDeviceCategory(),
          category: 'navigation',
          action: 'nav_subitem_click',
          source: 'navigation'
        })
        
        setPreviousPage(location.pathname)
        const profileUrl = buildProfileUrl(candidateId, candidateName, profileTab);
        navigate(profileUrl, { 
          state: { fromDashboard: true } 
        });
      }
      return;
    }

    const isFromNewDashboard = location.state?.fromDashboard
    const targetRoute = isFromNewDashboard && subItem.directRoute ? subItem.directRoute : subItem.route
    
    // Trackear click en subitem que navega a ruta
    fireAnalytics('nav_subitem_clicked', {
      edify_user_id: userData?.id || '',
      session_id: getSessionId(),
      nav_item_id: parentItem?.id || '',
      nav_item_label: parentItem?.label || '',
      nav_subitem_id: subItem.id,
      nav_subitem_label: subItem.label,
      nav_action: 'navigate_to_route',
      target_route: targetRoute,
      previous_page: getPreviousPage(),
      device_category: getDeviceCategory(),
      category: 'navigation',
      action: 'nav_subitem_click',
      source: 'navigation'
    })
    
    setPreviousPage(location.pathname)
    navigate(targetRoute, { 
      state: { fromDashboard: true } 
    })
  }

  const handleLogout = () => {
    openMainModal({
      title: "¿Deseas cerrar la sesión activa?",
      body: "Dejarán de llegarte las notificaciones de actividad",
      isSuccess: false,
      iconType: "warning",
      confirmHandler: () => { 
        logout(true, navigate)
        closeMainModal() 
      }
    })
  }

  const handleNavItemClick = (item: NavItem) => {
    handleRedirect(item)
  }

  useEffect(() => {
    setActiveProfileTab(null)
    const pathname = location.pathname
    const searchParams = new URLSearchParams(location.search)
    const currentTab = getProfileTabFromQuery(searchParams)
    
    if (pathname === '/dashboard') {
      setIsActive('dashboard')
    } else if (pathname === '/obrasprivadas/crear') {
      setIsActive('publicar')
      if (isNavExpanded) setExpandedSubmenus(new Set(['publicar']))
    } else if (pathname.startsWith('/obrasprivadas') || pathname.startsWith('/obraspublicas')) {
      setIsActive('explorar')
      if (isNavExpanded) setExpandedSubmenus(new Set(['explorar']))
    } else if (pathname === '/cotizaciondemateriales/estimacion-costos') {
      setIsActive('herramientas')
      if (isNavExpanded) setExpandedSubmenus(new Set(['herramientas']))
    } else if (pathname === '/cotizaciondemateriales/crear') {
      setIsActive('publicar')
      if (isNavExpanded) setExpandedSubmenus(new Set(['publicar']))
    } else if (pathname.startsWith('/cotizaciondemateriales')) {
      setIsActive('explorar')
      if (isNavExpanded) setExpandedSubmenus(new Set(['explorar']))
    } else if (pathname.startsWith('/profesionales')) {
      if (userData) {
        const profileUserId = profilePathInfo.userId
        const isOwnProfile = profileUserId && profileUserId === String(userData.id)
        if (isOwnProfile) {
          if (currentTab) setActiveProfileTab(currentTab)
          setIsActive(null)
          if (isNavExpanded) setExpandedSubmenus(new Set(['mis-contenidos']))
        } else {
          setIsActive('explorar')
          if (isNavExpanded) setExpandedSubmenus(new Set(['explorar']))
        }
      } else {
        setIsActive('explorar')
        if (isNavExpanded) setExpandedSubmenus(new Set(['explorar']))
      }
    } else if (pathname.startsWith('/mercado-de-trabajo')) {
      setIsActive('publicar')
      if (isNavExpanded) setExpandedSubmenus(new Set(['publicar']))
    } else if (pathname.startsWith('/bolsa-trabajo')) {
      setIsActive('explorar')
      if (isNavExpanded) setExpandedSubmenus(new Set(['explorar']))
    } else if (pathname.startsWith('/marketplace')) {
      setIsActive('explorar')
      if (isNavExpanded) setExpandedSubmenus(new Set(['explorar']))
    } else if (pathname.startsWith('/buscar-perfiles')) {
      setIsActive('explorar')
      if (isNavExpanded) setExpandedSubmenus(new Set(['explorar']))
    } else if (pathname.startsWith('/backoffice')) {
      setIsActive('admin')
      if (isNavExpanded) setExpandedSubmenus(new Set(['admin']))
    } else {
      setIsActive(null)
    }
  }, [location.pathname, location.search, isNavExpanded, userData, profilePathInfo.userId])


  return (
    <div className={styles.generalContainer}>
      <div className={`${styles.nav} ${isNavExpanded ? styles.navExpanded : ''}`}>


      <ul className={styles.navList}>
        {filteredNavItems.map((item) => (
          <li 
            key={item.id} 
            className={`${styles.navItemContainer} ${!isNavExpanded ? styles.navCollapsed : ''}`}
          >
            <div
              className={`${styles.navItem} ${isActive === item.id ? styles.navItemActive : ''} ${isSubItemActive(item) ? styles.navItemSubActive : ''}`}
              onClick={() => handleNavItemClick(item)}
              style={{ '--item-color': item.color } as React.CSSProperties}
            >
              <div className={styles.navItemIcon}>
                {isActive === item.id ? item.activeIcon : item.icon}
                {item.badge && item.badge > 0 && (
                  <span className={styles.navItemBadge}>{item.badge}</span>
                )}
              </div>
              
              {isNavExpanded && (
                <TruncatedText text={item.label} className={styles.navItemText} />
              )}

              {isNavExpanded && item.hasSubmenu && (
                <div className={styles.navItemArrow}>
                  {expandedSubmenus.has(item.id) ? 
                    <ChevronUp size={16} /> : 
                    <ChevronDown size={16} />
                  }
                </div>
              )}

            </div>

            {!item.hasSubmenu && (
              <div className={styles.hoverTooltip}>
                <div 
                  className={styles.hoverTooltipItem}
                  onClick={() => handleNavItemClick(item)}
                  style={{ '--item-color': item.color } as React.CSSProperties}
                >
                  <div className={styles.navSubItemIcon}>
                    {item.icon}
                  </div>
                  <TruncatedText text={item.label} className={styles.navSubItemText} />
                </div>
              </div>
            )}

            {item.hasSubmenu && item.subItems && (
              <ul 
                className={`${styles.navSubmenu} ${!isNavExpanded ? styles.hoverSubmenu : ''} ${isNavExpanded && expandedSubmenus.has(item.id) ? styles.navSubmenuVisible : ''} ${manuallyOpenedSubmenus.has(item.id) ? styles.navSubmenuWithAnimation : styles.navSubmenuNoAnimation}`}
              >
                {item.subItems.map((subItem) => {
                  const isSubItemCurrentlyActive = getActiveSubItem(item) === subItem.id
                  return (
                    <li
                      key={subItem.id}
                      className={`${styles.navSubItem} ${isSubItemCurrentlyActive ? styles.navSubItemActive : ''}`}
                      onClick={() => handleSubItemRedirect(subItem)}
                    >
                      <div className={styles.navSubItemIcon}>
                        {subItem.icon}
                      </div>
                      <TruncatedText text={subItem.label} className={styles.navSubItemText} />
                    </li>
                  )
                })}
              </ul>
            )}
          </li>
        ))}
      </ul>

      {/* Footer */}
      <div className={styles.navFooter}>
        <button
          className={`${styles.navToggle} ${!isNavExpanded ? styles.navCollapsed : ''}`}
          onClick={toggleNav}
        >
          {isNavExpanded ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
          {isNavExpanded && <span className={styles.navItemText}>Colapsar</span>}
          
          <div className={styles.navTooltip}>
            <div className={styles.simpleTooltip}>
              Expandir menú
            </div>
          </div>
        </button>

        <button
          className={`${styles.navLogout} ${!isNavExpanded ? styles.navCollapsed : ''}`}
          onClick={handleLogout}
        >
          <LogOut size={20} />
          {isNavExpanded && <span className={styles.navItemText}>Cerrar Sesión</span>}
          
          <div className={styles.navTooltip}>
            <div className={styles.simpleTooltip}>
              Cerrar Sesión
            </div>
          </div>
        </button>
      </div>
      </div>
    </div>
  )
}

const mapStateToProps = (state: any) => ({
  userData: state.general.userData,
  profileVisualizerData: state.general.profileVisualizer,
  sideNavBarExpanded: state.general.sideNavBarExpanded
})

const mapDispatchToProps = {
  logout,
  openMainModal,
  closeMainModal,
  openFeedTextModal,
  expandedSideBar
}

export default connect(mapStateToProps, mapDispatchToProps)(NewNavImproved)
